import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiChevronUp, FiChevronDown, FiX } from "react-icons/fi";
import {
  searchMessages,
  setFindInChatResults,
  findInChatNext,
  findInChatPrev,
  clearFindInChat,
} from "../../../Slices/chatSlice";

const ChatSearchBar = ({ conversationId, onClose }) => {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const dispatch = useDispatch();
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const findIndex = useSelector((state) => state.chat.findInChatIndex);
  const findResults = useSelector((state) => state.chat.findInChatResults);
  const searchLoading = useSelector((state) => state.chat.searchLoading);

  // Auto-focus on mount
  useEffect(() => {
    inputRef.current?.focus();
    return () => {
      dispatch(clearFindInChat());
    };
  }, [dispatch]);

  const doSearch = useCallback(
    (q) => {
      if (!q.trim() || q.trim().length < 2) {
        dispatch(clearFindInChat());
        return;
      }
      dispatch(
        searchMessages({
          userId: loginUser?.id,
          query: q.trim(),
          conversationId,
          limit: 200,
        })
      ).then((result) => {
        if (result.payload?.response) {
          const ids = result.payload.response.map((m) => m.id);
          dispatch(setFindInChatResults({ results: ids, query: q.trim() }));
        }
      });
    },
    [dispatch, loginUser?.id, conversationId]
  );

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (e.shiftKey) {
        dispatch(findInChatPrev());
      } else {
        dispatch(findInChatNext());
      }
    }
  };

  const total = findResults.length;
  const current = total > 0 ? findIndex + 1 : 0;

  return (
    <div className="chat-search-bar">
      <input
        ref={inputRef}
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder="Find in conversation..."
        className="chat-search-bar__input"
      />
      {query.trim().length >= 2 && (
        <span className="chat-search-bar__count">
          {searchLoading ? "..." : `${current} / ${total}`}
        </span>
      )}
      <button
        className="chat-search-bar__btn"
        onClick={() => dispatch(findInChatPrev())}
        disabled={total === 0}
        title="Previous (Shift+Enter)"
        type="button"
      >
        <FiChevronUp size={16} />
      </button>
      <button
        className="chat-search-bar__btn"
        onClick={() => dispatch(findInChatNext())}
        disabled={total === 0}
        title="Next (Enter)"
        type="button"
      >
        <FiChevronDown size={16} />
      </button>
      <button
        className="chat-search-bar__btn"
        onClick={onClose}
        title="Close (Esc)"
        type="button"
      >
        <FiX size={16} />
      </button>
    </div>
  );
};

export default ChatSearchBar;
